// ============================================================================
// STORY ROW
//
// One table row for a user story, plus its acceptance-criteria expansion row.
//
// Extracted because RequirementsPanel had three near-identical copies of this
// markup (the unassigned tab, the persona-grouped table, and the no-persona
// fallback) that had already drifted apart — the fallback lost its edit button
// and the persona table gained a clone button the others never got. Every new
// field on a story otherwise had to be added three times.
//
// The three call sites differ only in which columns they show, so `columns`
// drives both this component and StoryTableHead. Keeping the header and the
// body on one source of truth is what stops the expansion row's colSpan
// drifting out of step with the header and breaking the table layout.
// ============================================================================

import React from 'react';
import {
  ChevronUp, ChevronDown, CheckCircle, Pencil, Copy, Trash2, Tag, Paintbrush, Loader2,
  StickyNote, BookMarked, Lock, Unlock, Sparkles
} from 'lucide-react';
import { RaidItem, RaidStatus } from '../../types/raid';
import { Milestone } from '../../types/raid';
import { MilestonePicker } from './MilestonePicker';
import { RaidBadge } from './raid/RaidBadge';
import { RaidInlineList } from './raid/RaidInlineList';

export type StoryColumn = 'select' | 'id' | 'priority' | 'story' | 'actions';

/** The three layouts in use. Header and rows must be given the same one. */
export const STORY_COLUMNS = {
  /** Unassigned tab: no ID column, no clone, no open-design. */
  unassigned: ['select', 'priority', 'story', 'actions'] as const,
  /** Persona-grouped table: the full set. */
  persona: ['select', 'id', 'priority', 'story', 'actions'] as const,
  /** No-persona fallback: read-mostly, no selection. */
  bare: ['priority', 'story', 'actions'] as const,
};

export interface StoryLike {
  id: string;
  priority: string;
  acceptanceCriteria: string[];
  isNew?: boolean;
  tags?: string[];
  notes?: string[];
  source?: string;
  milestoneId?: string;
  locked?: boolean;
  lockedBy?: string;
  lockedAt?: string;
  [key: string]: any;
}

export function priorityConfig(priority: string) {
  switch (priority) {
    case 'high': return { pill: 'bg-red-100 text-red-700', label: 'High', dot: 'bg-red-500' };
    case 'medium': return { pill: 'bg-amber-100 text-amber-700', label: 'Medium', dot: 'bg-amber-500' };
    default: return { pill: 'bg-slate-100 text-slate-500', label: 'Low', dot: 'bg-slate-400' };
  }
}

export function storyText(story: any): string {
  const soThatSuffix = story.so_that ? ' so that ' + story.so_that : '';
  return story.story || (story.as_a ? `As ${story.as_a}, I want ${story.i_want || ''}${soThatSuffix}` : story.title || '');
}

export interface OpenDesignControls {
  isRunning: boolean;
  isPickerOpen: boolean;
  onTogglePicker: () => void;
  onStart: (skillId: string | null) => void;
}

const OPEN_DESIGN_OPTIONS: { label: string; skillId: string | null }[] = [
  { label: 'Prototype', skillId: null },
  { label: 'Deck', skillId: 'slides' },
  { label: 'Design System', skillId: 'design-brief' },
];

// ============================================================================
// Table header — same `columns` as the rows beneath it
// ============================================================================

export interface StoryTableHeadProps {
  columns: readonly StoryColumn[];
  /** Supplying these makes the ID, Priority and Story headers sortable. */
  sort?: {
    onSort: (col: 'id' | 'priority' | 'story') => void;
    icon: (col: 'id' | 'priority' | 'story') => React.ReactNode;
  };
  /** The persona table uses a tinted header; the others a plain one. */
  variant?: 'plain' | 'tinted';
}

export function StoryTableHead({ columns, sort, variant = 'plain' }: Readonly<StoryTableHeadProps>) {
  const labelClasses = 'flex items-center gap-0.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors';
  const staticClasses = 'text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider px-4 py-2.5';

  const header = (col: 'id' | 'priority' | 'story', label: string, width: string) => {
    if (sort) {
      return (
        <th key={col} className={`text-left px-4 py-2.5 ${width}`}>
          <button onClick={() => sort.onSort(col)} className={labelClasses}>
            {label} {sort.icon(col)}
          </button>
        </th>
      );
    }
    return <th key={col} className={`${staticClasses} ${width}`}>{label}</th>;
  };

  return (
    <thead>
      <tr className={variant === 'tinted' ? 'bg-slate-50 border-b border-slate-200' : 'border-b border-slate-100'}>
        {columns.map(col => {
          switch (col) {
            case 'select': return <th key={col} className="w-8 px-3 py-2.5" />;
            case 'id': return header('id', 'ID', 'w-20');
            case 'priority': return header('priority', 'Priority', sort ? 'w-28' : 'w-24');
            case 'story': return header('story', 'Story', '');
            default: return <th key={col} className="w-10 px-2 py-2.5" />;
          }
        })}
      </tr>
    </thead>
  );
}

// ============================================================================
// Row
// ============================================================================

export interface StoryRowProps {
  story: StoryLike;
  columns: readonly StoryColumn[];
  /** The persona table is roomier than the other two. */
  density?: 'compact' | 'roomy';
  isExpanded: boolean;
  isSelected?: boolean;
  onToggleExpand: (id: string) => void;
  onToggleSelect?: (id: string) => void;
  onEdit?: (story: StoryLike) => void;
  onClone?: (story: StoryLike) => void;
  onDelete: (story: StoryLike) => void;
  /** Omitted where locking isn't wired up for this table. */
  onToggleLock?: (story: StoryLike) => void;
  /** Omitted where avatar review isn't wired up for this table. */
  onAvatarReview?: (story: StoryLike) => void;
  openDesign?: OpenDesignControls;
  /** Omitted where RAID is not wired up, e.g. the read-only project rollup. */
  raid?: RaidRowControls;
  /** Omitted where there is no project context, so no phases to assign. */
  milestone?: {
    milestones: Milestone[];
    onChange: (milestoneId: string | null) => void;
    disabled?: boolean;
  };
}

export interface RaidRowControls {
  items: RaidItem[];
  openCount: number;
  blockingCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  onAdd: () => void;
  onEditItem: (item: RaidItem) => void;
  onResolveItem: (raidId: string, status: RaidStatus, resolution: string) => void;
  saving?: boolean;
}

export function StoryRow({
  story,
  columns,
  density = 'compact',
  isExpanded,
  isSelected = false,
  onToggleExpand,
  onToggleSelect,
  onEdit,
  onClone,
  onDelete,
  onToggleLock,
  onAvatarReview,
  openDesign,
  raid,
  milestone,
}: Readonly<StoryRowProps>) {
  const pc = priorityConfig(story.priority);
  const text = storyText(story);
  const pad = density === 'roomy' ? 'py-5' : 'py-3';
  const hasCriteria = story.acceptanceCriteria.length > 0;
  const noteCount = story.notes?.length ?? 0;
  const hasExpandableContent = hasCriteria || noteCount > 0;
  const isLocked = !!story.locked;

  // The expansion row lines up under the Story column: one blank cell per
  // column before it, then a span covering Story and everything after.
  const storyIndex = columns.indexOf('story');
  const leadingBlanks = storyIndex === -1 ? 0 : storyIndex;
  const expansionSpan = storyIndex === -1 ? columns.length : columns.length - storyIndex;

  const cell = (col: StoryColumn) => {
    switch (col) {
      case 'select':
        return (
          <td key={col} className={`px-3 ${pad} align-top`}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelect?.(story.id)}
              className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
            />
          </td>
        );

      case 'id':
        return (
          <td key={col} className={`px-4 ${pad} align-top`}>
            <span className="text-[10px] font-mono text-slate-400 whitespace-nowrap">{story.id}</span>
          </td>
        );

      case 'priority':
        return (
          <td key={col} className={`px-4 ${pad} align-top`}>
            {columns.includes('id') ? (
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${pc.dot} shrink-0`} />
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${pc.pill}`}>{pc.label}</span>
              </div>
            ) : (
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${pc.pill}`}>{pc.label}</span>
            )}
            {story.isNew && columns.includes('id') && (
              <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/10 text-primary uppercase tracking-wider">New</span>
            )}
          </td>
        );

      case 'story':
        return (
          <td key={col} className={`px-4 ${pad} align-top`}>
            <p className="text-slate-800 font-medium leading-snug">{text}</p>
            {(isLocked || (story.tags && story.tags.length > 0) || milestone) && (
              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                {isLocked && (
                  <span
                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
                    title={story.lockedBy ? `Locked by ${story.lockedBy}` : 'Locked'}
                  >
                    <Lock size={9} />Locked
                  </span>
                )}
                {story.tags?.map((tag: string) => (
                  <span key={tag} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                    <Tag size={9} />{tag}
                  </span>
                ))}
                {milestone && (
                  <MilestonePicker
                    milestones={milestone.milestones}
                    value={story.milestoneId}
                    onChange={milestone.onChange}
                    disabled={milestone.disabled || isLocked}
                  />
                )}
              </div>
            )}
            {story.source && (
              <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-400" title={story.source}>
                <BookMarked size={9} className="shrink-0" />
                <span className="truncate max-w-[420px]">{story.source}</span>
              </p>
            )}
            <div className="flex items-center gap-3">
              {hasCriteria && (
                <button
                  onClick={() => onToggleExpand(story.id)}
                  className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400 hover:text-primary transition-colors"
                >
                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {story.acceptanceCriteria.length} acceptance criteria
                </button>
              )}
              {noteCount > 0 && (
                <span className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
                  <StickyNote size={11} />
                  {noteCount} {noteCount === 1 ? 'note' : 'notes'}
                </span>
              )}
              {raid && (
                <RaidBadge
                  count={raid.items.length}
                  openCount={raid.openCount}
                  blockingCount={raid.blockingCount}
                  isExpanded={raid.isExpanded}
                  onToggle={raid.onToggle}
                  onAdd={raid.onAdd}
                />
              )}
            </div>
            {raid?.isExpanded && raid.items.length > 0 && (
              <RaidInlineList
                items={raid.items}
                saving={raid.saving}
                onResolve={raid.onResolveItem}
                onEdit={raid.onEditItem}
                onAdd={raid.onAdd}
              />
            )}
          </td>
        );

      default:
        return (
          <td key={col} className={`px-2 ${pad} align-top`}>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onAvatarReview && (
                <button
                  onClick={() => onAvatarReview(story)}
                  className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                  title="Ask avatars to review"
                >
                  <Sparkles size={14} />
                </button>
              )}
              {onToggleLock && (
                <button
                  onClick={() => onToggleLock(story)}
                  className={`p-1 rounded transition-colors ${isLocked ? 'text-amber-600 hover:bg-amber-50' : 'text-slate-400 hover:bg-amber-50 hover:text-amber-600'}`}
                  title={isLocked ? 'Unlock story' : 'Lock story'}
                >
                  {isLocked ? <Lock size={14} /> : <Unlock size={14} />}
                </button>
              )}
              {onEdit && !isLocked && (
                <button
                  onClick={() => onEdit(story)}
                  className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                  title="Edit story"
                >
                  <Pencil size={14} />
                </button>
              )}
              {onClone && (
                <button
                  onClick={() => onClone(story)}
                  className="p-1 rounded hover:bg-teal-50 text-slate-400 hover:text-teal-600 transition-colors"
                  title="Clone story"
                >
                  <Copy size={14} />
                </button>
              )}
              {openDesign && (
                <div className="relative">
                  <button
                    onClick={openDesign.onTogglePicker}
                    disabled={openDesign.isRunning}
                    className="p-1 rounded hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 disabled:opacity-50"
                    title="Start Open Design task"
                  >
                    {openDesign.isRunning ? <Loader2 size={14} className="animate-spin" /> : <Paintbrush size={14} />}
                  </button>
                  {openDesign.isPickerOpen && (
                    <div className="absolute top-full right-0 mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                      {OPEN_DESIGN_OPTIONS.map(({ label, skillId }) => (
                        <button
                          key={label}
                          onClick={() => openDesign.onStart(skillId)}
                          className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!isLocked && (
                <button
                  onClick={() => onDelete(story)}
                  className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                  title="Remove story"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </td>
        );
    }
  };

  return (
    <React.Fragment>
      <tr className={`group hover:bg-slate-50 transition-colors ${story.priority === 'low' ? 'opacity-70' : ''} ${isSelected ? 'bg-primary/[0.03]' : ''}`}>
        {columns.map(cell)}
      </tr>
      {isExpanded && hasExpandableContent && (
        <tr className="bg-slate-50">
          {Array.from({ length: leadingBlanks }, (_, i) => <td key={`blank-${columns[i]}`} />)}
          <td className="px-4 pb-3 pt-1" colSpan={expansionSpan}>
            <ul className="space-y-1">
              {story.acceptanceCriteria.map((c) => (
                <li key={c} className="flex items-start gap-2 text-xs text-slate-600">
                  <CheckCircle size={12} className="text-primary mt-0.5 shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
            {noteCount > 0 && (
              <ul className="space-y-1 mt-2 pt-2 border-t border-slate-200">
                {story.notes!.map((n: string) => (
                  <li key={n} className="flex items-start gap-2 text-xs text-slate-500 italic">
                    <StickyNote size={12} className="text-slate-400 mt-0.5 shrink-0" />
                    {n}
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}
